import { ENV } from "@/constant";
import { canUseLocalStorage } from "@/utils/canuseLocalStorage";
import { creationProductManager } from "./creation-product-manager";
import { agentManager } from "./agent-manager";

/**
 * 最后访问路由管理器
 * 负责管理用户最后访问的路由，提供缓存、获取、跳转等功能
 * @author 杨俊朗
 */
export class RouteLastVisitManager {
  private static instance: RouteLastVisitManager;
  private readonly STORAGE_KEY = "MIUTA_LAST_VISIT";
  private readonly LAST_ASSETS_ROUTE_KEY = "MIUTA_LAST_VISIT_ASSETS";
  // private readonly DEFAULT_ROUTE = "/creation/folder/uncategorized/";
  private readonly DEFAULT_ROUTE = "/agent/";
  private creationProductManager = creationProductManager;
  private agentManager = agentManager;
  private store: string = "";

  private constructor() {
    this.init();
  }

  /**
   * 获取单例实例
   */
  public static getInstance(): RouteLastVisitManager {
    if (!RouteLastVisitManager.instance) {
      RouteLastVisitManager.instance = new RouteLastVisitManager();
    }
    return RouteLastVisitManager.instance;
  }

  /**
   * 初始化 - 从 localStorage 读取最后访问路由
   */
  private init(): void {
    if (!canUseLocalStorage()) {
      return;
    }
    const store = localStorage.getItem(this.STORAGE_KEY);
    try {
      this.update(store);
    } catch (error) {
      console.error("Failed to parse last visit route:", error);
      this.update("");
    }
  }

  /**
   * 更新最后访问路由
   * @param route 路由路径
   */
  public update(route: string): void {
    // 需要缓存的访问路径
    const caches = [
      // "/creation/product/",
      // "/creation/folder/",
      // "/creation/project/",
      "/settings/account/",
      // "/settings/members/",
      "/assets/material/",
      "/assets/knowledge/",
      "/assets/material/:id/",
      "/assets/knowledge/:id/",
      "/assets/actor/",
      "/assets/voice/",
      "/agent/",
      "/unsubscribe/",
      "/agent/dashboard/",
      // "/subscription-plan/",
      // "/settings/subscription/",
      "/agent/creation/",
      "/agent/dashboard/permission/",
      "/agent/dashboard/data-overview/",
      "/agent/dashboard/content-management/",
      "/agent/dashboard/order-management/",
      "/agent/dashboard/company-asset/",
      "/agent/dashboard/operation-log/",
      "/select-system/",
    ];
    if (!caches.some((item) => route?.startsWith(item))) {
      this.store = "";
      localStorage.removeItem(this.STORAGE_KEY);
      return;
    }
    this.store = route;
    // 存储到 localStorage
    if (!canUseLocalStorage()) {
      return;
    }
    if (route) {
      localStorage.setItem(this.STORAGE_KEY, route);
    } else {
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  /**
   * 更新最后访问资产路由
   * @param route 路由路径
   */
  public updateLastVisitAssets(route: string): void {
    if (!route.startsWith("/assets/")) {
      return;
    }
    if (canUseLocalStorage() && route) {
      localStorage.setItem(this.LAST_ASSETS_ROUTE_KEY, route);
    } else {
      localStorage.removeItem(this.LAST_ASSETS_ROUTE_KEY);
    }
  }

  /**
   * 获取最后访问资产路由
   */
  public getLastVisitAssets(): string {
    if (!canUseLocalStorage()) {
      return "";
    }
    const stored = localStorage.getItem(this.LAST_ASSETS_ROUTE_KEY);
    try {
      return stored ? stored : '/assets/material/';
    } catch (error) {
      console.error(
        "Failed to parse last visit assets route from localStorage:",
        error
      );
      return '/assets/material/';
    }
  }

  /**
   * 解析路径类型和ID
   * @param route 路由路径
   * @returns 解析结果
   */
  private parseRoute(route: string): {
    type: 'folder' | 'project' | 'other';
    id?: string;
    hasTrailingSlash: boolean;
  } {
    // 标准化路径，确保以斜杠开头
    const normalizedRoute = route.startsWith('/') ? route : `/${route}`;

    // 匹配 /creation/folder/ 或 /creation/folder/:id/ 或 /creation/folder/:id
    const folderMatch = normalizedRoute.match(/^\/creation\/folder\/(([^/]+)\/?)?$/);
    if (folderMatch) {
      return {
        type: 'folder',
        id: folderMatch[2] || undefined, // 提取ID（如果存在）
        hasTrailingSlash: normalizedRoute.endsWith('/')
      };
    }

    // 匹配 /creation/project/ 或 /creation/project/:id/ 或 /creation/project/:id
    const projectMatch = normalizedRoute.match(/^\/creation\/project\/(([^/]+)\/?)?$/);
    if (projectMatch) {
      return {
        type: 'project',
        id: projectMatch[2] || undefined, // 提取ID（如果存在）
        hasTrailingSlash: normalizedRoute.endsWith('/')
      };
    }

    // 匹配 /agent/creation/:id/ 或 /agent/creation/:id
    const agentCreationMatch = normalizedRoute.match(/^\/agent\/creation\/(([^/]+)\/?)?$/);
    if (agentCreationMatch) {
      return {
        type: 'other',
        id: agentCreationMatch[2] || undefined, // 提取ID（如果存在）
        hasTrailingSlash: normalizedRoute.endsWith('/')
      };
    }

    return {
      type: 'other',
      hasTrailingSlash: normalizedRoute.endsWith('/')
    };
  }

  /**
   * 获取最后访问路由
   */
  public async get(): Promise<string> {
    // 路由不存在则设置路由为默认路由（会走创作区块逻辑 先加载文件夹系统 根据实际情况判定跳转路由 实在没有才跳默认路由）
    if (!this.store) {
      this.store = this.DEFAULT_ROUTE;
    }
    // 非主站：不沿用缓存的 /select-system/，回退默认路由
    if (this.store === '/select-system/' && !ENV.isMain) {
      this.store = this.DEFAULT_ROUTE;
    }
    // 访问的是智能体页面
    if (this.store === "/agent/") {
      const agentId = this.agentManager.getAgentId();
      if (agentId) {
        return `/agent/creation/${agentId}/`;
      } else {
        this.store = this.DEFAULT_ROUTE;
      }
    }
    // 其他路径
    return this.store;
  }

  /**
   * 清除最后访问路由
   */
  public clear(): void {
    this.update("");
  }
}

// 导出单例实例
export const routeLastVisitManager = RouteLastVisitManager.getInstance();
